import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import slugify from "slugify";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { saveProductSchema, variantSchema, MIN_IMAGES, MAX_IMAGES, META_TITLE_MIN, META_TITLE_MAX, META_DESC_MIN, META_DESC_MAX, SLUG_REGEX } from "./products-schema";

async function assertStaff(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.from("user_roles").select("role").eq("user_id", ctx.userId);
  if (error) throw new Error("Operation failed.");
  if (!data || data.length === 0) throw new Error("Forbidden");
}

function makeSlug(s: string) {
  return slugify(s || "", { lower: true, strict: true, trim: true }).slice(0, 100) || "product";
}

async function uniqueSlug(sb: any, base: string, ignoreId?: string) {
  let slug = base;
  for (let i = 2; i < 200; i++) {
    const q = sb.from("products").select("id").eq("slug", slug).limit(1);
    const { data } = await q;
    if (!data || data.length === 0 || data[0].id === ignoreId) return slug;
    slug = `${base}-${i}`.slice(0, 100);
  }
  return `${base}-${Date.now().toString(36)}`.slice(0, 100);
}

/**
 * Insert `row` into `products` and, if we lose a slug uniqueness race
 * (Postgres 23505), regenerate a suffixed slug and retry a few times.
 */
async function insertProductWithSlugRetry(
  sb: any,
  row: Record<string, any>,
  baseSlug: string,
): Promise<any> {
  let attempt = 0;
  let currentSlug = row.slug ?? baseSlug;
  while (attempt < 6) {
    const { data, error } = await sb
      .from("products")
      .insert({ ...row, slug: currentSlug })
      .select("*")
      .single();
    if (!error) return data;
    if ((error as any).code === "23505") {
      attempt += 1;
      currentSlug = `${baseSlug}-${attempt + 1}`.slice(0, 100);
      continue;
    }
    throw error;
  }
  throw new Error("Slug collision; could not allocate unique slug.");
}

async function signImages(sb: any, rows: any[]) {
  // Admin editor: use short-lived signed URLs directly from the private
  // product-images bucket. We can't use the public /api/public/img proxy here
  // because it only serves images belonging to *published* products — draft
  // products (freshly created / with new renders) would 404 in the editor.
  const out: any[] = [];
  for (const r of rows) {
    let signed_url: string | null = null;
    try {
      const { data } = await sb.storage
        .from("product-images")
        .createSignedUrl(r.storage_path, 60 * 60); // 1h
      signed_url = data?.signedUrl ?? null;
    } catch {
      signed_url = null;
    }
    out.push({
      ...r,
      signed_url,
      signed_url_sm: signed_url,
      signed_url_thumb: signed_url,
    });
  }
  return out;
}

function proxyImg(storagePath: string, w?: number, h?: number, q?: number): string {
  const encoded = storagePath.split("/").map(encodeURIComponent).join("/");
  const params = new URLSearchParams();
  if (w) params.set("w", String(w));
  if (h) params.set("h", String(h));
  if (q) params.set("q", String(q));
  const qs = params.toString();
  return `/api/public/img/${encoded}${qs ? `?${qs}` : ""}`;
}

// ---------- list ----------
export const listProducts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) =>
    z
      .object({
        status: z.enum(["all", "draft", "published"]).default("all"),
        q: z.string().max(120).default(""),
        brand: z.string().max(40).default(""),
      })
      .partial()
      .parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    let q = sb.from("products").select("*").order("updated_at", { ascending: false }).limit(200);
    if (data?.status && data.status !== "all") q = q.eq("status", data.status);
    if (data?.brand) q = q.eq("brand", data.brand);
    const needle = (data?.q ?? "").replace(/[^\p{L}\p{N}\s\-']/gu, " ").trim();
    if (needle) q = q.or(`title.ilike.%${needle}%,sku.ilike.%${needle}%,slug.ilike.%${needle}%`);
    const { data: rows, error } = await q;
    if (error) throw new Error("Failed to load products.");
    // attach first image (signed)
    const ids = (rows ?? []).map((r: any) => r.id);
    let imgs: any[] = [];
    if (ids.length) {
      const { data: i } = await sb.from("product_images").select("product_id, storage_path, position").in("product_id", ids).order("position", { ascending: true });
      imgs = i ?? [];
    }
    const firstByPid: Record<string, string> = {};
    for (const im of imgs) if (!firstByPid[im.product_id]) firstByPid[im.product_id] = im.storage_path;
    // Sign thumbnails in parallel — serial signing was the main page slowdown.
    const out = await Promise.all(
      (rows ?? []).map(async (r: any) => {
        const sp = firstByPid[r.id];
        let thumb: string | null = null;
        if (sp) {
          try {
            const { data: s } = await sb.storage.from("product-images").createSignedUrl(sp, 60 * 60);
            thumb = s?.signedUrl ?? null;
          } catch { thumb = null; }
        }
        return { ...r, thumbnail_url: thumb };
      }),
    );
    return out;
  });

// ---------- paged list (infinite scroll on /admin/products) ----------
export const listProductsPage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) =>
    z
      .object({
        status: z.enum(["all", "draft", "published"]).default("all"),
        q: z.string().max(120).default(""),
        offset: z.number().int().min(0).default(0),
        limit: z.number().int().min(1).max(50).default(8),
        sortBy: z.enum(["sku", "updated", "brand"]).default("updated"),
        sortAsc: z.boolean().default(false),
      })
      .partial()
      .parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const offset = data?.offset ?? 0;
    const limit = data?.limit ?? 8;
    const LIST_COLS = "id,sku,slug,title,brand,status,price_cents,updated_at,created_at";
    const sortBy = data?.sortBy ?? "updated";
    const asc = data?.sortAsc ?? false;
    let q = sb.from("products").select(LIST_COLS, { count: "exact" });
    if (sortBy === "sku") {
      // Natural numeric SKU order (N999 < N1039) via the generated sku_num
      // column, then alphabetical prefix / full SKU as tiebreaker.
      q = q
        .order("sku_num", { ascending: asc })
        .order("sku", { ascending: asc, nullsFirst: false });
    } else if (sortBy === "brand") {
      q = q
        .order("brand", { ascending: asc, nullsFirst: false })
        .order("sku_num", { ascending: asc })
        .order("sku", { ascending: asc });
    } else {
      q = q.order("updated_at", { ascending: asc });
    }
    q = q.order("id", { ascending: true }).range(offset, offset + limit - 1);
    if (data?.status && data.status !== "all") q = q.eq("status", data.status);
    if (data?.q) q = q.or(`title.ilike.%${data.q}%,sku.ilike.%${data.q}%,slug.ilike.%${data.q}%`);
    const { data: rows, error, count } = await q;
    if (error) throw new Error("Failed to load products.");
    const ids = (rows ?? []).map((r: any) => r.id);
    let imgs: any[] = [];
    if (ids.length) {
      const { data: i } = await sb
        .from("product_images")
        .select("product_id, storage_path, position")
        .in("product_id", ids)
        .order("position", { ascending: true });
      imgs = i ?? [];
    }
    const firstByPid: Record<string, string> = {};
    for (const im of imgs) if (!firstByPid[im.product_id]) firstByPid[im.product_id] = im.storage_path;
    // Sign thumbnails in parallel — serial signing was the main page slowdown.
    const items = await Promise.all(
      (rows ?? []).map(async (r: any) => {
        const sp = firstByPid[r.id];
        let thumb: string | null = null;
        if (sp) {
          try {
            const { data: s } = await sb.storage.from("product-images").createSignedUrl(sp, 60 * 60);
            thumb = s?.signedUrl ?? null;
          } catch { thumb = null; }
        }
        return { ...r, thumbnail_url: thumb };
      }),
    );
    const total = count ?? 0;
    return { items, total, hasMore: offset + items.length < total };
  });

// ---------- get ----------
export const getProduct = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const [{ data: p, error: pe }, { data: im }, { data: vr }] = await Promise.all([
      sb.from("products").select("*").eq("id", data.id).single(),
      sb.from("product_images").select("*").eq("product_id", data.id).order("position", { ascending: true }),
      sb.from("product_variants").select("*").eq("product_id", data.id).order("position", { ascending: true }),
    ]);
    if (pe || !p) throw new Error("Product not found.");
    const images = await signImages(sb, im ?? []);
    return { product: p, images, variants: vr ?? [] };
  });

// ---------- create draft ----------
const SKU_REGEX = /^N\d{4,8}$/;

async function suggestNextSku(sb: any): Promise<string> {
  const { data } = await sb
    .from("products")
    .select("sku")
    .ilike("sku", "N%")
    .order("sku", { ascending: false })
    .limit(50);
  let max = 1000;
  for (const r of data ?? []) {
    const m = /^N(\d+)$/.exec(r.sku ?? "");
    if (m) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
  }
  return `N${max + 1}`;
}

export const peekNextSku = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    return { sku: await suggestNextSku(sb) };
  });

// Returns distinct custom model IDs (`${brandId}::${label}`) used across all products,
// so the compatibility panel can suggest previously-added custom models for reuse.
export const listCustomModels = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const { data, error } = await sb.from("products").select("model_ids").limit(5000);
    if (error) throw new Error("Failed to load custom models.");
    const seen = new Map<string, { id: string; brand: string; label: string }>();
    for (const row of data ?? []) {
      const arr: string[] = Array.isArray(row.model_ids) ? row.model_ids : [];
      for (const mid of arr) {
        if (typeof mid !== "string" || !mid.includes("::")) continue;
        if (seen.has(mid)) continue;
        const [brand, ...rest] = mid.split("::");
        const label = rest.join("::");
        if (!brand || !label) continue;
        seen.set(mid, { id: mid, brand, label });
      }
    }
    return { models: Array.from(seen.values()) };
  });

export const createProductDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) =>
    z.object({ sku: z.string().trim().regex(SKU_REGEX, "SKU must be N + digits, e.g. N1001").optional() }).parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const userId = (context as any).userId;
    let sku = data?.sku?.trim();
    if (sku) {
      const { data: clash } = await sb.from("products").select("id").eq("sku", sku).limit(1);
      if (clash && clash.length) throw new Error("SKU already exists.");
    } else {
      sku = await suggestNextSku(sb);
    }
    const baseSlug = `${sku.toLowerCase()}-draft`;
    const slug = await uniqueSlug(sb, baseSlug);
    try {
      const row = await insertProductWithSlugRetry(
        sb,
        {
          sku,
          slug,
          title: "",
          status: "draft",
          created_by: userId,
          // English-first defaults for the Specs section (see SpecsEditor)
          condition: "new",
          availability: "in_stock",
          material: "ABS",
          weight_grams: 50,
          country_of_origin: "BG",
          warranty_months: 24,
          return_days: 14,
        },
        baseSlug,
      );
      return row;
    } catch (err) {
      console.error("createProductDraft", err);
      throw new Error("Failed to create draft.");
    }
  });

// ---------- save ----------
export const saveProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => saveProductSchema.parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const payload: any = { ...data };
    delete payload.id;
    if (payload.slug) {
      payload.slug = await uniqueSlug(sb, makeSlug(payload.slug), data.id);
    } else if (payload.title) {
      const { data: cur } = await sb.from("products").select("slug,sku").eq("id", data.id).single();
      if (cur && (cur.slug ?? "").endsWith("-draft")) {
        payload.slug = await uniqueSlug(sb, makeSlug(payload.title), data.id);
      }
    }
    // Normalize oem_numbers: trim, uppercase, dedupe, drop empties
    if (Array.isArray(payload.oem_numbers)) {
      const seen = new Set<string>();
      payload.oem_numbers = payload.oem_numbers
        .map((s: string) => (s ?? "").toString().trim().toUpperCase())
        .filter((s: string) => {
          if (!s) return false;
          if (seen.has(s)) return false;
          seen.add(s);
          return true;
        })
        .slice(0, 40);
    }
    // Normalize faqs: trim, drop rows that are missing question or answer,
    // so half-typed drafts don't block autosave of the rest of the form.
    if (Array.isArray(payload.faqs)) {
      payload.faqs = payload.faqs
        .map((f: any) => ({
          q: (f?.q ?? "").toString().trim(),
          a: (f?.a ?? "").toString().trim(),
        }))
        .filter((f: any) => f.q.length >= 3 && f.a.length >= 3)
        .slice(0, 20);
    }
    const { data: row, error } = await sb.from("products").update(payload).eq("id", data.id).select("*").single();
    if (error) {
      console.error("saveProduct", error);
      throw new Error("Failed to save.");
    }
    return row;
  });

// ---------- publish / unpublish ----------
export const publishProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const [{ data: p }, { data: imgs }, { data: vr }, { count: approvedCount }] = await Promise.all([
      sb.from("products").select("*").eq("id", data.id).single(),
      sb.from("product_images").select("alt_text").eq("product_id", data.id),
      sb.from("product_variants").select("id").eq("product_id", data.id),
      sb.from("product_reviews").select("id", { count: "exact", head: true })
        .eq("product_id", data.id).eq("status", "approved"),
    ]);
    if (!p) throw new Error("Not found.");
    const errs: string[] = [];
    if (!p.title || p.title.length < 8) errs.push("title");
    if (!p.slug || !SLUG_REGEX.test(p.slug)) errs.push("slug");
    if (!p.price_cents) errs.push("price");
    if (!p.brand) errs.push("brand");
    if (!p.model_ids?.length) errs.push("models");
    if ((p.meta_title?.length ?? 0) < META_TITLE_MIN || (p.meta_title?.length ?? 0) > META_TITLE_MAX) errs.push("meta_title");
    if ((p.meta_description?.length ?? 0) < META_DESC_MIN || (p.meta_description?.length ?? 0) > META_DESC_MAX) errs.push("meta_description");
    const imgCount = imgs?.length ?? 0;
    if (imgCount < MIN_IMAGES || imgCount > MAX_IMAGES) errs.push("images");
    if ((imgs ?? []).some((i: any) => (i.alt_text ?? "").length < 10)) errs.push("alt");
    if (p.variant_kind && p.variant_kind !== "none" && (vr?.length ?? 0) < 1) errs.push("variants");
    if ((approvedCount ?? 0) < 3) errs.push("reviews (≥3 approved)");
    if (errs.length) throw new Error(`Cannot publish: ${errs.join(", ")}`);
    const { data: row, error } = await sb
      .from("products")
      .update({ status: "published", published_at: new Date().toISOString() })
      .eq("id", data.id)
      .select("*")
      .single();
    if (error) throw new Error("Failed to publish.");
    return row;
  });

export const unpublishProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    await sb.from("products").update({ status: "draft", published_at: null }).eq("id", data.id);
    return { ok: true };
  });

export const deleteProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const { data: imgs } = await sb.from("product_images").select("storage_path").eq("product_id", data.id);
    if (imgs?.length) {
      await sb.storage.from("product-images").remove(imgs.map((i: any) => i.storage_path));
    }
    await sb.from("products").delete().eq("id", data.id);
    return { ok: true };
  });

// ---------- images ----------
export const signProductImageUpload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) =>
    z.object({ product_id: z.string().uuid(), filename: z.string().min(1).max(200) }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const safe = data.filename.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${data.product_id}/${crypto.randomUUID()}-${safe}`;
    const { data: up, error } = await sb.storage.from("product-images").createSignedUploadUrl(path);
    if (error) throw new Error("Failed to sign upload.");
    return { path, token: up.token, signedUrl: up.signedUrl };
  });

export const registerProductImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) =>
    z
      .object({
        product_id: z.string().uuid(),
        storage_path: z.string(),
        alt_text: z.string().max(220).default(""),
        width: z.number().int().nullable().optional(),
        height: z.number().int().nullable().optional(),
        source: z.enum(["upload", "render"]).default("upload"),
        source_ref: z.string().uuid().nullable().optional(),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const { count } = await sb
      .from("product_images")
      .select("*", { count: "exact", head: true })
      .eq("product_id", data.product_id);
    const { data: row, error } = await sb
      .from("product_images")
      .insert({
        product_id: data.product_id,
        storage_path: data.storage_path,
        alt_text: data.alt_text,
        width: data.width ?? null,
        height: data.height ?? null,
        source: data.source,
        source_ref: data.source_ref ?? null,
        position: count ?? 0,
      })
      .select("*")
      .single();
    if (error) throw new Error("Failed to save image.");
    let signed_url: string | null = null;
    try {
      const { data: s } = await sb.storage.from("product-images").createSignedUrl(row.storage_path, 60 * 60);
      signed_url = s?.signedUrl ?? null;
    } catch { signed_url = null; }
    return { ...row, signed_url };
  });

export const updateProductImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => z.object({ id: z.string().uuid(), alt_text: z.string().max(220).optional(), position: z.number().int().optional() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const payload: any = {};
    if (data.alt_text !== undefined) payload.alt_text = data.alt_text;
    if (data.position !== undefined) payload.position = data.position;
    const { error } = await sb.from("product_images").update(payload).eq("id", data.id);
    if (error) throw new Error("Failed to update.");
    return { ok: true };
  });

export const reorderProductImages = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => z.object({ product_id: z.string().uuid(), ordered_ids: z.array(z.string().uuid()).max(50) }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    for (let i = 0; i < data.ordered_ids.length; i++) {
      await sb.from("product_images").update({ position: i }).eq("id", data.ordered_ids[i]).eq("product_id", data.product_id);
    }
    return { ok: true };
  });

export const deleteProductImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const { data: row } = await sb.from("product_images").select("storage_path").eq("id", data.id).single();
    if (row?.storage_path) await sb.storage.from("product-images").remove([row.storage_path]);
    await sb.from("product_images").delete().eq("id", data.id);
    return { ok: true };
  });

// ---------- variants ----------
export const upsertVariant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => variantSchema.extend({ product_id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const payload: any = { ...data };
    if (!payload.id) delete payload.id;
    // Compute absolute price from base + delta
    const { data: prod } = await sb
      .from("products")
      .select("price_cents")
      .eq("id", data.product_id)
      .single();
    const base = prod?.price_cents ?? 0;
    const delta = Number.isFinite(payload.price_delta_cents) ? payload.price_delta_cents : 0;
    payload.price_delta_cents = delta;
    payload.price_cents = Math.max(0, base + delta);
    const { data: row, error } = await sb.from("product_variants").upsert(payload).select("*").single();
    if (error) throw new Error("Failed to save variant.");
    return row;
  });

// Recompute every variant for a product (used after manual base price tweaks
// from non-trigger paths, and for safety after AI/import bulk edits).
export const recomputeVariantPrices = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => z.object({ product_id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const { data: prod } = await sb
      .from("products")
      .select("price_cents")
      .eq("id", data.product_id)
      .single();
    const base = prod?.price_cents ?? 0;
    const { data: vrs } = await sb
      .from("product_variants")
      .select("id, price_delta_cents")
      .eq("product_id", data.product_id);
    for (const v of vrs ?? []) {
      const price = Math.max(0, base + (v.price_delta_cents ?? 0));
      await sb.from("product_variants").update({ price_cents: price }).eq("id", v.id);
    }
    return { ok: true };
  });

// Duplicate a product — clone fields, variants and images (storage objects copied).
export const duplicateProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) =>
    z.object({ id: z.string().uuid(), sku: z.string().trim().toUpperCase().optional() }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const userId = (context as any).userId;
    const [{ data: p }, { data: imgs }, { data: vrs }] = await Promise.all([
      sb.from("products").select("*").eq("id", data.id).single(),
      sb.from("product_images").select("*").eq("product_id", data.id).order("position"),
      sb.from("product_variants").select("*").eq("product_id", data.id).order("position"),
    ]);
    if (!p) throw new Error("Not found.");
    let newSku: string;
    if (data.sku) {
      if (!SKU_REGEX.test(data.sku)) {
        throw new Error("Невалиден SKU. Формат: N + 4 до 8 цифри (напр. N1250).");
      }
      const { data: exists } = await sb.from("products").select("id").eq("sku", data.sku).maybeSingle();
      if (exists) throw new Error(`SKU ${data.sku} вече се използва.`);
      newSku = data.sku;
    } else {
      newSku = await suggestNextSku(sb);
    }
    const baseSlug = `${newSku.toLowerCase()}-draft`;
    const newSlug = await uniqueSlug(sb, baseSlug);
    const insertRow: any = { ...p };
    delete insertRow.id;
    delete insertRow.created_at;
    delete insertRow.updated_at;
    delete insertRow.sku_num; // generated column — cannot be inserted
    insertRow.sku = newSku;
    insertRow.slug = newSlug;
    insertRow.status = "draft";
    insertRow.published_at = null;
    insertRow.created_by = userId;
    insertRow.title = p.title ? `${p.title} (copy)` : "";
    let newProd: any;
    try {
      newProd = await insertProductWithSlugRetry(sb, insertRow, baseSlug);
    } catch (e: any) {
      throw new Error(e?.message ? `Неуспешно дублиране: ${e.message}` : "Неуспешно дублиране.");
    }
    // Variants
    for (const v of vrs ?? []) {
      const row: any = { ...v };
      delete row.id;
      delete row.created_at;
      delete row.updated_at;
      row.product_id = newProd.id;
      await sb.from("product_variants").insert(row);
    }
    // Images — copy storage objects so deletes don't cascade
    for (const im of imgs ?? []) {
      const { data: blob } = await sb.storage.from("product-images").download(im.storage_path);
      if (!blob) continue;
      const buf = new Uint8Array(await blob.arrayBuffer());
      const ext = (im.storage_path.split(".").pop() || "webp").toLowerCase();
      const newPath = `${newProd.id}/${crypto.randomUUID()}.${ext}`;
      const ct = ext === "png" ? "image/png" : ext === "jpg" || ext === "jpeg" ? "image/jpeg" : "image/webp";
      const { error: upErr } = await sb.storage.from("product-images").upload(newPath, buf, { contentType: ct, upsert: false });
      if (upErr) continue;
      const insertImg: any = { ...im };
      delete insertImg.id;
      delete insertImg.created_at;
      delete insertImg.updated_at;
      insertImg.product_id = newProd.id;
      insertImg.storage_path = newPath;
      await sb.from("product_images").insert(insertImg);
    }
    return newProd;
  });

export const deleteVariant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    await sb.from("product_variants").delete().eq("id", data.id);
    return { ok: true };
  });

// ---------- render → product ----------
export const importRendersToProduct = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: any) =>
    z
      .object({
        product_id: z.string().uuid(),
        output_ids: z.array(z.string().uuid()).min(1).max(20),
        mode: z.enum(["append", "replace"]).default("append"),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);
    const sb = (context as any).supabase;
    const { data: prod } = await sb.from("products").select("title, brand").eq("id", data.product_id).single();
    const { data: outs, error } = await sb.from("render_outputs").select("*").in("id", data.output_ids);
    if (error) throw new Error("Failed to read renders.");
    if (data.mode === "replace") {
      const { data: imgs } = await sb.from("product_images").select("id, storage_path").eq("product_id", data.product_id);
      if (imgs?.length) {
        await sb.storage.from("product-images").remove(imgs.map((i: any) => i.storage_path));
        await sb.from("product_images").delete().eq("product_id", data.product_id);
      }
    }
    const { count } = await sb
      .from("product_images")
      .select("*", { count: "exact", head: true })
      .eq("product_id", data.product_id);
    let pos = count ?? 0;
    const created: any[] = [];
    for (const o of outs ?? []) {
      // download then upload to product-images
      const { data: blob, error: dlErr } = await sb.storage.from("render-output").download(o.storage_path);
      if (dlErr || !blob) continue;
      const buf = new Uint8Array(await blob.arrayBuffer());
      const ext = o.format === "jpeg" ? "jpg" : o.format ?? "webp";
      const newPath = `${data.product_id}/render-${o.id}.${ext}`;
      const contentType = ext === "png" ? "image/png" : ext === "jpg" ? "image/jpeg" : "image/webp";
      const { error: upErr } = await sb.storage.from("product-images").upload(newPath, buf, { contentType, upsert: true });
      if (upErr) continue;
      const alt = `${prod?.title || prod?.brand || "FIXHEN"} — render ${o.sequence}`.slice(0, 200);
      const { data: row } = await sb
        .from("product_images")
        .insert({
          product_id: data.product_id,
          storage_path: newPath,
          alt_text: alt,
          width: o.width,
          height: o.height,
          source: "render",
          source_ref: o.id,
          position: pos++,
        })
        .select("*")
        .single();
      if (row) created.push(row);
    }
    return { ok: true, count: created.length };
  });

// Public read for shop page
export const getPublishedProduct = createServerFn({ method: "GET" })
  .validator((d: any) => z.object({ slug: z.string().max(100) }).parse(d))
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const sb = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const { data: p } = await sb.from("products").select("*").eq("slug", data.slug).eq("status", "published").maybeSingle();
    if (!p) return null;
    const [{ data: imgs }, { data: vr }, { data: reviewRows }] = await Promise.all([
      sb.from("product_images").select("*").eq("product_id", p.id).order("position", { ascending: true }),
      sb.from("product_variants").select("*").eq("product_id", p.id).order("position", { ascending: true }),
      sb.from("product_reviews")
        .select("rating, title, body, author_display_name, created_at, verified_purchase, helpful_count, is_seeded")
        .eq("product_id", p.id)
        .eq("status", "approved")
        .order("helpful_count", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(50),
    ]);
    // Structured data must only ever contain reviews written by real customers.
    // AI-seeded reviews stay visible on the page but are excluded from JSON-LD
    // (both the review list and the aggregate rating) to stay within Google's
    // review-snippet policy.
    const organicRows = (reviewRows ?? []).filter((r: any) => !r.is_seeded);
    const organicRating = organicRows.length
      ? {
          count: organicRows.length,
          value: Number(
            (organicRows.reduce((sum: number, r: any) => sum + Number(r.rating || 0), 0) / organicRows.length).toFixed(1),
          ),
        }
      : null;
    // signed URLs for images
    const images: any[] = [];
    for (const im of imgs ?? []) {
      images.push({
        ...im,
        signed_url: proxyImg(im.storage_path, 800, 800, 82),
        signed_url_sm: proxyImg(im.storage_path, 400, 400, 78),
        signed_url_thumb: proxyImg(im.storage_path, 160, 160, 72),
      });
    }
    // Sanitize description ONCE on the server so SSR and CSR emit the exact same
    // HTML string inside dangerouslySetInnerHTML. Running DOMPurify in the
    // component would use jsdom on the server and native DOMParser in the
    // browser, whose outputs differ subtly (attribute order, whitespace) and
    // trigger a hydration mismatch inside a Suspense boundary → React #419.
    const { enhanceDescriptionHtml } = await import("@/lib/description/enhance-description");
    // Use `sanitize-html` (pure JS) instead of DOMPurify — DOMPurify relies on
    // jsdom on the server, which crashes on Cloudflare Workers with
    // "Cannot read properties of undefined (reading 'bind')".
    const { default: sanitizeHtml } = await import("sanitize-html");
    const description_html = sanitizeHtml(enhanceDescriptionHtml(p.description ?? ""), {
      allowedTags: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h2", "h3", "h4", "blockquote", "a", "span", "code", "pre"],
      allowedAttributes: { a: ["href", "target", "rel"] },
      allowedSchemes: ["http", "https", "mailto", "tel"],
      allowedSchemesByTag: {},
      allowProtocolRelative: false,
    });
    // Promote heading levels so the description's section titles slot in
    // directly under the page <h1> (product title) without skipping <h2>.
    // Order matters: promote h3→h2 first would double-promote, so do h2→h3
    // safeguard then h4→h3 then h3→h2 using a placeholder token.
    const promoted = description_html
      .replace(/<(\/?)h4(\b[^>]*)>/gi, "<$1h3$2>")
      .replace(/<(\/?)h3(\b[^>]*)>/gi, "<$1h2$2>");
    return {
      product: { ...p, description_html: promoted },
      images,
      variants: vr ?? [],
      organicRating,
      topReviews: organicRows.slice(0, 5).map((r: any) => ({
        rating: r.rating,
        title: r.title,
        body: r.body,
        author: r.author_display_name ?? "Customer",
        date: r.created_at,
        verified: r.verified_purchase,
      })),
    };
  });

// Public listing of published products for the /shop page.
// Returns rows shaped for the shop catalog (Product-like) with a signed thumbnail.
export const listPublishedProducts = createServerFn({ method: "GET" })
  .handler(async () => {
    const { createClient } = await import("@supabase/supabase-js");
    const sb = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const { data: rows, error } = await sb
      .from("products")
      .select("id, sku, slug, title, brand, model_ids, category, price_cents, currency, oem_numbers, rating_value, rating_count, availability, published_at, updated_at")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(500);
    if (error) return [];
    const ids = (rows ?? []).map((r: any) => r.id);
    const imgByPid: Record<string, string> = {};
    if (ids.length) {
      const { data: imgs } = await sb
        .from("product_images")
        .select("product_id, storage_path, position")
        .in("product_id", ids)
        .order("position", { ascending: true });
      for (const im of imgs ?? []) if (!imgByPid[im.product_id]) imgByPid[im.product_id] = im.storage_path;
    }
    const out: Array<{
      id: string; sku: string; slug: string; name: string; brand: string;
      models: string[]; category: string; price: number; currency: string;
      oem: string[]; inStock: boolean; rating: number; reviewCount: number;
      badges: string[]; addedAt: string; image: string | null;
    }> = [];
    for (const r of rows ?? []) {
      const sp = imgByPid[r.id];
      const image: string | null = sp ? proxyImg(sp, 400, 400, 78) : null;
      out.push({
        id: r.id,
        sku: r.sku,
        slug: r.slug,
        name: r.title,
        brand: (r.brand ?? "").toLowerCase(),
        models: Array.isArray(r.model_ids) ? r.model_ids : [],
        category: r.category ?? "all",
        price: (r.price_cents ?? 0) / 100,
        currency: r.currency ?? "EUR",
        oem: Array.isArray(r.oem_numbers) ? r.oem_numbers : [],
        inStock: (r.availability ?? "in_stock") === "in_stock",
        rating: Number(r.rating_value ?? 0),
        reviewCount: r.rating_count ?? 0,
        badges: [],
        addedAt: r.published_at ?? r.updated_at ?? new Date().toISOString(),
        image,
      });
    }
    return out;
  });
